import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApiNoConversion } from '~/services/kyClient';

import { queryKeysDataSubscription } from './queryKeys';
import { type MutateContactPointData } from './types';
import { contactPointToApi } from './userKeyedFields';

type ContactPointResponse = {
  code: number;
  id: UUID;
  message: string;
};

/**
 * Creates a contact point.
 *
 * @param {MutateContactPointData} contactPointData - An object containing the contact point details to be created.
 * @returns {Promise<ContactPointResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/create_contact_point_data_subscription_contact_point_post
 */
export const createContactPoint = async (
  contactPointData: MutateContactPointData,
) => {
  const response = await vestigasApiNoConversion
    .post(ENDPOINT.DATA_SUBSCRIPTION.CREATE_CONTACT_POINT(), {
      json: contactPointToApi(contactPointData),
    })
    .json<ContactPointResponse>();

  return response;
};

/**
 * Custom hook for creating a contact point using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationCreateContactPoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactPointData: MutateContactPointData) =>
      createContactPoint(contactPointData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSubscription.getAllContactPoints(),
      });
    },
  });
};
